/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography'
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../reducers';
import { filterProjectionSystems, getProjectionSystemsById, pointsTransform, showProjectionSystemDialog } from '../../actions/mapContent';
import { dialogActionsStyle, fieldsetWidthStyle, radioStandardStyle, spinnerContainerStyle } from '../../shared/styles';
import DialogActions from '@mui/material/DialogActions';
import MikeProjectionSelect from '../mike-projection-select';
import IProjection from '../mike-projection-select/model/IProjection';
import CircularProgress from '@mui/material/CircularProgress';
import { defineMessages, useIntl } from 'react-intl';
import { READER } from '../../shared/constants';
import { importFile } from '../../actions/imports';
import { pendingImportSet, setLoadingBathymetry } from '../../actions/createMesh';
import { removeProgressItem } from '../../actions/progressItems';
import xyzReaderParameterTranslations from './xyzReaderParameterTranslations';
import ContextHelp from '../ContextHelp';
import mikeSharedTheme from '../../styles/mikeSharedTheme';
import { isNumeric } from '../../helpers/common';
import MikeDialogTitle from '../DialogComponents/MikeDialogTitle';
import MikeButton from '../mike-button';

const titleStyle = css`
  padding: ${mikeSharedTheme.spacing(1)};
  z-index: 11;
  border-radius: 4px 4px 0px 0px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.16);
  height: ${mikeSharedTheme.spacing(8)};
  color: ${mikeSharedTheme.palette.background.paper};
  width: 100%;
`;

const dialogContentStyle = css`
  padding-top: 0;
  background-color: ${mikeSharedTheme.palette.background.default}; 
  
`;

const contentContainerStyle = css`
   padding: ${mikeSharedTheme.spacing(1)};  
`
const labelStyle = css`
   padding-top: ${mikeSharedTheme.spacing(2)};
   padding-bottom: ${mikeSharedTheme.spacing(2)};
`
const verticalDistanceStyle= css`
  padding-top: ${mikeSharedTheme.spacing(2)};
`
const titleContainer = css`
  display: flex;
`
const xyzTitleStyle = css`
  padding-top: ${mikeSharedTheme.spacing(1)};
  padding-bottom: ${mikeSharedTheme.spacing(0)};
`
const subTitleStyle = css` 
  &.MuiTypography-body1 {
    font-size: 0.75rem;
    &.MuiTypography-root {
      color: ${mikeSharedTheme.palette.darkGrey.main};
    }
  }
`


   const radioGroupSX = {    
      color: mikeSharedTheme.palette.primary.main,
      '& .MuiTypography-root, .MuiTypography-root.Mui-disabled, .MuiFormControlLabel-root': {
        color: mikeSharedTheme.palette.primary.main
      }
    };


interface IProps {
  epsgCode: number;   
}
const ProjectionSystemDialog = (props: IProps) => {
  const { epsgCode } = props;
  const dispatch = useDispatch();
  const intl = useIntl();  

  const type: READER = useSelector(
    (state: IState) => state.mapContent.projectionSystemDialogForReaderType
  );  

  const messages = useMemo(() => {
    switch (type){
      case READER.XY: {
        return defineMessages({
          title: { id: 'projectionSystemDialog.xyReader.title' },
          question: { id: 'projectionSystemDialog.xyReader.question' },
          yes: { id: 'projectionSystemDialog.xyReader.yes' },
          no: { id: 'projectionSystemDialog.xyReader.no' },         
        })
      }
      case READER.ZIPPEDSHAPE: {
        return defineMessages({
          title: { id: 'projectionSystemDialog.zippedShapeReader.title' },
          question: { id: 'projectionSystemDialog.zippedShapeReader.question' },
          yes: { id: 'projectionSystemDialog.zippedShapeReader.yes' },
          no: { id: 'projectionSystemDialog.zippedShapeReader.no' },         
        })
      }
      case READER.BATHYMETRY: {
        return defineMessages({
          title: { id: 'projectionSystemDialog.bathymetryReader.title' },
          question: { id: 'projectionSystemDialog.bathymetryReader.question' },
          yes: { id: 'projectionSystemDialog.bathymetryReader.yes' },
          no: { id: 'projectionSystemDialog.bathymetryReader.no' },         
        })
      }
    }
  }, [type]) 

  const isOpen: boolean = useSelector(
    (state: IState) => state.mapContent.showProjectionSystemDialog
  );  

  const pendingImport  = useSelector(
    (state: IState) => state.createMesh.pendingImport
  );  

  const uploadingPoints = useSelector(
    (state: IState) => state.mapContent.uploadingPoints
  );  

  const coordSystems: Array<IProjection> = useSelector(
    (state: IState) => state.mapContent.selectedProjectionSystems
  );  
 
  const searchProjectionSystemsById: boolean = useSelector(
    (state: IState) => state.mapContent.searchProjectionSystemsById
  );  

  const loadingCoordSystems = useSelector(
    (state: IState) => state.mapContent.loadingCoordinateSystems
  );  

  const [selectedEPSG, setSelectedEPSG] = useState(epsgCode)

  useEffect(() => {
    setSelectedEPSG(epsgCode)   
  }, [epsgCode]); 

  const handleCancel = useCallback((_event?, reason?) => {
    if(!reason || (reason !== 'backdropClick' && reason !== 'escapeKeyDown')) {
      if (type !== READER.XY && pendingImport){
        dispatch(pendingImportSet(null))
        dispatch(removeProgressItem({id: pendingImport.id, title: "", progressValue: 0}))
      }      
      dispatch(showProjectionSystemDialog(false))
  }
  }, [dispatch, type, pendingImport])

  const [hasTargetSRID, setHasTargetSRID] = useState(true)
  const [xyzColumnOption, setXyzColumnOption] = useState('')

  const handleChange = (event: { target: { value: string }; }) => {   
    setHasTargetSRID(event.target.value === 'true')
  }

  const handleChangeXYZOption= (event: { target: { value: string }; }) => {   
    setXyzColumnOption(event.target.value)
  }

  const onProjectionSearchTextChanged = (searchText: string) => {
    isNumeric(searchText) ?
      dispatch(getProjectionSystemsById(Number(searchText))) :
      dispatch(filterProjectionSystems(searchText))
  };

  const onProjectionSelected = (epsgCode: number) => {
    setSelectedEPSG(epsgCode)
  }

  const onOk = useCallback(() => {
    const inputEPSG = hasTargetSRID ? epsgCode : selectedEPSG
    switch (type){
      case READER.XY: {
        dispatch(pointsTransform(inputEPSG))
        break;
      }
      case READER.BATHYMETRY:{       
        const { id, fileName, fileUrl, projectId, importData, importType } = pendingImport  
        const xyzParameter = {...importData, xyzColumnSequence: xyzColumnOption}
        dispatch(setLoadingBathymetry())
        dispatch(importFile(
          id,
          fileName,
          fileUrl,
          projectId,
          {...xyzParameter, coordinateSystemId: inputEPSG},
          importType
        ))
        break;
      }
      case READER.ZIPPEDSHAPE: {
        const { id, fileName, fileUrl, projectId, importData, importType } = pendingImport       
        dispatch(importFile(
          id,
          fileName,
          fileUrl,
          projectId,
          {...importData, coordinateSystemId: inputEPSG},
          importType
        ))
        break;
      }
      
    }
    dispatch(showProjectionSystemDialog(false))
   
  }, [type, dispatch, hasTargetSRID, epsgCode, selectedEPSG, pendingImport, xyzColumnOption])

  const getOKButtonIsEnabled = useCallback(() => {
    const projectionDefined = hasTargetSRID || epsgCode !== selectedEPSG
    if (type === READER.BATHYMETRY){
      if (projectionDefined){
        return xyzColumnOption !== undefined
      }
    }   
    return projectionDefined
  }, [hasTargetSRID, epsgCode, selectedEPSG, type, xyzColumnOption] )

  return (
    <Dialog maxWidth={'sm'} onClose={handleCancel} open={isOpen} >
      <MuiDialogTitle css={titleStyle}>
        <MikeDialogTitle title={intl.formatMessage(messages.title)} onClose={handleCancel} />
      </MuiDialogTitle>
      <MuiDialogContent css={dialogContentStyle}>
        <div css={contentContainerStyle}> 
          <div css={titleContainer}>
            <Typography css={labelStyle} variant="body2">{intl.formatMessage(messages.question)}</Typography>
            <ContextHelp helpTexts={[intl.formatMessage({id: 'projectionSystemDialog.pickProjection'})]}/>
          </div>
          
          <FormControl component="fieldset" css={fieldsetWidthStyle}>
            <RadioGroup aria-label="fileformat" name="fileformat" value={hasTargetSRID} onChange={handleChange} sx={radioGroupSX}> 
              <FormControlLabel value={true} control={<Radio color="primary" css={radioStandardStyle} />} label={intl.formatMessage(messages.yes)} />               
              <FormControlLabel value={false} control={<Radio color="primary" css={radioStandardStyle} />} label={intl.formatMessage(messages.no)} /> 
              {loadingCoordSystems ? <div css={spinnerContainerStyle}><CircularProgress/></div>:
               <MikeProjectionSelect               
                onProjectionSelected={onProjectionSelected}
                onProjectionSearchTextChanged={onProjectionSearchTextChanged}
                projections={coordSystems}
                projectionsLoading={loadingCoordSystems}
                searchById={searchProjectionSystemsById}
              />}
          </RadioGroup>
        </FormControl>
          {type === READER.BATHYMETRY && 
            <div css={verticalDistanceStyle}>
              <div css={titleContainer}>
                <div>
                  <Typography
                    variant="body2"
                    css={xyzTitleStyle}
                  >
                    {xyzReaderParameterTranslations.title}
                  </Typography>
                  <Typography css={subTitleStyle}>
                    {xyzReaderParameterTranslations.subTitle}
                  </Typography>
                </div>
                <ContextHelp helpTexts={[xyzReaderParameterTranslations.info]}/>
              </div>
              <FormControl component="fieldset">
                <RadioGroup aria-label="fileformat" name="fileformat" value={xyzColumnOption} onChange={handleChangeXYZOption} css={radioGroupSX}> 
                  <FormControlLabel value={'xycz'} control={<Radio color="primary" css={radioStandardStyle} />} label={xyzReaderParameterTranslations.xyczOption} />               
                  <FormControlLabel value={'xyzc'} control={<Radio color="primary" css={radioStandardStyle} />} label={xyzReaderParameterTranslations.xyzcOption} /> 
                  <FormControlLabel value={'xyz'} control={<Radio color="primary" css={radioStandardStyle} />} label={xyzReaderParameterTranslations.xyzOption} />  
              </RadioGroup>
            </FormControl>
            </div>
          }        
        </div>
    </MuiDialogContent>
    <DialogActions css={dialogActionsStyle}>
        <MikeButton onClick={handleCancel} variant="text">
          {"Cancel"}
        </MikeButton>
        <MikeButton active={uploadingPoints} onClick={onOk} color="primary" disabled={!getOKButtonIsEnabled()}>
          {"Upload"}
        </MikeButton>
      </DialogActions>
  </Dialog>
  );
};

export default ProjectionSystemDialog;